/**
 * User menu dropdown component.
 *
 * Shows user avatar and provides access to account settings and logout.
 */

import { User, Settings, LogOut } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useStores } from '@/stores/StoreContext';

interface UserMenuProps {
  /** Custom class name */
  className?: string;
}

/**
 * User menu dropdown.
 *
 * Shows when user is authenticated, provides access to:
 * - Account settings
 * - Logout
 */
export const UserMenu = observer(function UserMenu({
  className,
}: UserMenuProps): React.ReactElement | null {
  const { authStore } = useStores();

  // Only show for authenticated users
  if (!authStore.isAuthenticated || !authStore.user) {
    return null;
  }

  const { user } = authStore;
  const initials =
    user.displayName
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) ?? 'U';

  const handleLogout = async (): Promise<void> => {
    await authStore.logout();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className={`relative h-10 w-10 rounded-full ${className ?? ''}`}>
          <Avatar className="h-10 w-10">
            {user.avatarUrl && (
              <AvatarImage src={user.avatarUrl} alt={user.displayName ?? 'User'} />
            )}
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user.displayName ?? 'User'}</p>
            {user.email && (
              <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/account" className="flex cursor-pointer items-center">
            <Settings className="mr-2 h-4 w-4" />
            <span>Account Settings</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => void handleLogout()}
          className="cursor-pointer text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

/**
 * Guest indicator shown when user is not logged in.
 */
export const GuestIndicator = observer(function GuestIndicator(): React.ReactElement {
  const { authStore } = useStores();

  if (authStore.isAuthenticated) {
    return <></>;
  }

  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <User className="h-4 w-4" />
      <span>Guest</span>
    </div>
  );
});
